import {
  createHTTP,
  HTTPEndpointInput,
  HTTPError,
  HTTPRequestOptions,
} from '@superdispatch/http';
import { URITemplateParams } from '@superdispatch/uri';
import { Schema } from 'yup';

export interface APISuccessResponse<Data> {
  meta: {
    status: 'success';
  };
  data: Data;
}

export interface APIErrorResponse {
  meta: { status: 'error' };
  message: string;
}

export interface APIFailureResponse {
  meta: {
    status: 'fail';
  };
  data: {
    message: string;
    details: [
      {
        loc: string[];
        msg: string;
        type: string;
        ctx?: {
          enum_values?: string[];
        };
      },
    ];
  };
}

export type APIError = Error & APIFailureResponse['data'];

export function isAPIError(error: unknown): error is APIError {
  return error instanceof Error && 'details' in error;
}

export const http = createHTTP({
  baseURL:
    import.meta.env.VITE_APP_TARGET === 'production'
      ? 'https://pricing-insights.superdispatch.com/api'
      : 'https://pricing-insights.staging.superdispatch.org/api',
});

export function requestJSON<
  TData,
  TParams extends URITemplateParams = URITemplateParams,
>(
  input: HTTPEndpointInput<TParams>,
  options?: HTTPRequestOptions & { schema?: Schema },
): Promise<TData> {
  return http
    .requestJSON<APISuccessResponse<TData>>(input, options)
    .then((data) => {
      if (options?.schema) {
        return options.schema.cast(data);
      }
      return data;
    })
    .catch((error) => {
      if (error instanceof HTTPError) {
        if (error.response.headers.get('cf-mitigated') === 'challenge') {
          window.location.reload();
          return Promise.reject(
            new Error('Request blocked by Cloudflare. Please try again.'),
          );
        }

        return error.response
          .json()
          .then((response: APIFailureResponse | APIErrorResponse) => {
            if ('data' in response) {
              const error = new Error(response.data.message);
              throw Object.assign(error, response.data);
            }

            throw new Error(response.message);
          });
      }

      throw new Error(error.message || 'Failed to fetch');
    });
}
